import { getApps } from 'firebase/app'
import { auth } from '../../firebaseConfig'
import { breadcrumb } from '../../utils/sentry'
import { clearAuth } from '../../utils/auth'

const RETRY_TIMEOUT = 3 * 1000 // 3 sec
const REFRESH_TIMEOUT = 30 * 60 * 1000 // 30 min
const MIN_REFRESH_INTERVAL = 10 * 1000 // 10 sec
const EVENT_DEBOUNCE_DELAY = 500 // 500ms debounce for iOS events

class TokenRefresher {
    /**
     * TokenRefresher
     * ---------------
     * A singleton utility that keeps the Firebase ID token of the currently
     * authenticated user fresh throughout the lifetime of the page.
     *
     * Features
     * --------
     * 1. **Automatic refresh**
     *    • Forces a token refresh every 30 minutes (`REFRESH_TIMEOUT`).
     * 2. **Retry logic**
     *    • If the user is not yet available or a refresh fails, schedules a retry
     *      after 10 seconds (`RETRY_TIMEOUT`).
     * 3. **Tab-visibility awareness**
     *    • Whenever the browser tab regains focus (`visibilitychange → visible`),
     *      the class validates the token immediately to avoid using an expired
     *      credential after a long period in the background.
     * 4. **Re-entrancy protection**
     *    • Concurrent callers share one in-flight renewal and receive its result.
     * 5. **Breadcrumb logging**
     *    • Logs every significant step (`start`, scheduled refresh, retry, success,
     *      failure, visibility change) both to the console and to an external
     *      monitoring utility via `addBreadcrumb`.
     *
     * Implementation Notes
     * --------------------
     * • Uses `setTimeout` instead of `setInterval` so that each new schedule is
     *   created only after the previous refresh attempt finishes.
     * • The constructor is private to enforce the singleton pattern; obtain the
     *   instance via `TokenRefresher.getInstance()`.
     * • All timers are cleared before re-scheduling to prevent multiple pending
     *   callbacks.
     */

    private static instance: TokenRefresher

    private timeoutId: NodeJS.Timeout | null = null
    // The renewal currently in progress, shared by every concurrent caller; null when idle.
    private inFlight: Promise<boolean> | null = null
    private lastRefreshTime: number = 0
    private eventDebounceTimeoutId: NodeJS.Timeout | null = null
    private isIOS: boolean = false

    private constructor() {
        this.isIOS =
            /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
        this.setupEventListeners()
    }

    public static getInstance(): TokenRefresher {
        if (!TokenRefresher.instance) {
            TokenRefresher.instance = new TokenRefresher()
        }
        return TokenRefresher.instance
    }

    public async start() {
        if (this.inFlight) {
            this.log('[TokenRefresher] Already refreshing, skipping start')
            return
        }
        this.log('[TokenRefresher] Starting token refresher')
        await this.refresh()
    }

    /**
     * Renew the Firebase ID token.
     *
     * Resolves true only when this call (or the in-flight one it joined) actually obtained a new
     * token. Every path that declines to renew — the throttle, the Medicore iframe, no signed-in
     * user, a Firebase failure — resolves false, so a caller acting on a 401 can tell a renewal
     * from a no-op. It used to resolve the same way in all of those cases, which left the API
     * client retrying a request with the credential that had just been rejected (PROD-REACT-SX).
     *
     * @param force Renew even inside MIN_REFRESH_INTERVAL. The throttle exists to stop the timers
     *     and visibility events churning; a caller holding a 401 has proof the token is bad, and
     *     is the one caller the throttle must not silently ignore.
     */
    public async refresh({ force = false }: { force?: boolean } = {}): Promise<boolean> {
        // The throttle is answered per caller, before the shared renewal below. Deciding it inside
        // that renewal would let a throttled timer tick become the promise a forced caller joins,
        // handing its "no" to the one caller holding proof that the token needs replacing.
        if (!force && Date.now() - this.lastRefreshTime < MIN_REFRESH_INTERVAL) {
            this.log('Refresh call skipped (refresh is allowed only once per 10 seconds)')
            return false
        }

        // Single-flight. Several requests can fail with a 401 at once — an audio batch uploads in
        // parallel — and each used to call in, where all but the first became a no-op that still
        // resolved as though it had renewed. Every caller that reaches here intends to renew, so
        // sharing one renewal gives them all the same true answer.
        this.inFlight ??= this.performRefresh().finally(() => {
            this.inFlight = null
        })
        return this.inFlight
    }

    private async performRefresh(): Promise<boolean> {
        const user = auth.currentUser
        if (!user) {
            this.log('User not ready.')
            this.log('Apps in registry', { app_count: getApps().length })
            this.scheduleRetry()
            return false
        }

        this.lastRefreshTime = Date.now()

        try {
            await user.getIdToken(true)
            this.log('Token has been refreshed successfully.')
            this.scheduleRefresh()
            return true
        } catch (e: unknown) {
            const code = e instanceof Error && 'code' in e ? (e as { code?: string }).code : undefined
            switch (code) {
                case 'auth/network-request-failed':
                    console.warn('Network request failed:', e)
                    break
                case 'auth/user-token-expired':
                    console.warn('Token expired and cannot be refreshed, so the user is logged out.', e)
                    clearAuth()
                    break
                default:
                    console.error('Refresh failed:', e)
                    break
            }
            if (code !== 'auth/user-token-expired') {
                // if we has cleared the auth state, we should not retry to refresh the token
                this.scheduleRetry()
            }
            return false
        }
    }

    private debouncedRefresh(): void {
        // Clear any existing debounced refresh
        if (this.eventDebounceTimeoutId) {
            clearTimeout(this.eventDebounceTimeoutId)
        }

        // On iOS, focus and visibility events can overlap or fire in rapid succession
        const debounceDelay = this.isIOS ? EVENT_DEBOUNCE_DELAY : 100

        this.eventDebounceTimeoutId = setTimeout(async () => {
            this.eventDebounceTimeoutId = null
            await this.refresh()
        }, debounceDelay)
    }

    private scheduleRefresh(): void {
        this.clearTimeout()
        this.log('Refresh scheduled', { delayMinutes: REFRESH_TIMEOUT / 60000 })
        this.timeoutId = setTimeout(async () => {
            this.timeoutId = null
            await this.refresh()
        }, REFRESH_TIMEOUT)
    }

    private scheduleRetry(): void {
        // Only clear the main timeout, not the debounce timeout
        if (this.timeoutId) {
            clearTimeout(this.timeoutId)
            this.timeoutId = null
        }
        this.log(`Schedule retry`, { retry_timeout_sec: RETRY_TIMEOUT / 1000 })
        this.timeoutId = setTimeout(async () => {
            this.timeoutId = null
            await this.refresh()
        }, RETRY_TIMEOUT)
    }

    private clearTimeout(): void {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId)
            this.timeoutId = null
        }

        if (this.eventDebounceTimeoutId) {
            clearTimeout(this.eventDebounceTimeoutId)
            this.eventDebounceTimeoutId = null
        }
    }

    private setupEventListeners(): void {
        this.log('Setting up event listeners', { isIOS: this.isIOS })

        // Use debounced refresh for event-triggered refreshes to prevent rapid successive calls
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.log('Visibility changed to visible')
                this.debouncedRefresh()
            }
        })

        // On iOS, focus events can fire very frequently during multitasking
        // Use debounced refresh to prevent stack overflow
        window.addEventListener('focus', () => {
            // Only trigger refresh if the tab is visible to avoid hidden background refreshes
            if (document.visibilityState === 'visible') {
                this.log('Focus event received (visible)')
                this.debouncedRefresh()
            }
        })

        // Additional iOS-specific handling for pageshow event
        // This helps with iOS Safari's tab management
        if (this.isIOS) {
            window.addEventListener('pageshow', (event) => {
                this.log('Pageshow (iOS)', { persisted: event.persisted })
                // Only refresh if the page was restored from cache
                if (event.persisted) {
                    this.debouncedRefresh()
                }
            })
        }
    }

    private log(message: string, obj: Record<string, unknown> | null = null) {
        console.info('[TokenRefresher] ' + message, obj || '')
        breadcrumb('TokenRefresher', message, obj ?? {})
    }
}

export const tokenRefresher = TokenRefresher.getInstance()
